import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { firstValueFrom, take } from 'rxjs';

import { FeedApiService } from 'src/app/api/services/feed-api.service';

import { toggleFeedView } from '../state/feed.actions';
import { FeedAllowedViewsValues } from '../models/core.models';
import { selectFeed } from '../state';

/**
 * Which panes a feed shows (`list` / `analytics` / `details`).
 *
 * A service rather than a mutation hook per call site because **the request
 * payload is state the dispatch just produced**: the store is updated first, and
 * the PATCH sends whatever `feed.views` became - the caller's own
 * `view`/`selected` values are never used to build the body. Four components
 * toggle views, and duplicating that dispatch-then-read dance in all four is how
 * call sites drift.
 */
@Injectable({ providedIn: 'root' })
export class FeedViewsService {
  private readonly store = inject(Store);
  private readonly feedApi = inject(FeedApiService);

  /**
   * Show or hide one pane: update the store, then persist whatever `feed.views`
   * became. Order matters - the PATCH reads the post-dispatch array.
   */
  async toggleView(
    feedId: string,
    view: FeedAllowedViewsValues,
    selected: boolean,
  ): Promise<void> {
    this.store.dispatch(toggleFeedView({ feedId, view, selected }));

    const feed = await firstValueFrom(
      this.store.select(selectFeed).pipe(take(1)),
    );
    if (!feed) return;

    try {
      await firstValueFrom(
        this.feedApi.saveFeedViewsState(feed.id, feed.views),
      );
    } catch {
      // * a failed views PATCH is silent by design
    }
  }
}
